import type { SectionPlanInfo, SectionType, WorkoutPlanInfoId } from '@az3fr/core';
import { createID } from '@az3fr/core';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { sectionTypeDisplayInfos } from '@/entities/section-plan/model/section-type';

/**
 * A Pure function that creates a new, default section plan entity.
 * It uses the central `sectionTypeDisplayInfos` rulebook to set the
 * correct initial values for a give section type.
 */
function createNewSectionPlan(payload: {
	workoutPlanId: WorkoutPlanInfoId;
	sectionType: SectionType;
	orderInWorkoutPlan: number;
}): SectionPlanInfo {
	const { workoutPlanId, sectionType, orderInWorkoutPlan } = payload;
	const now = new Date().toISOString();

	// Get the default rules from our central configuration
	const { coreModel } = sectionTypeDisplayInfos[sectionType];

	// Get the default input values from the core model
	const { rounds, durationSeconds, intervalSeconds } = coreModel.getDefaultInputValues();

	return {
		id: createID('sectionPlan'),
		workoutPlanId,
		sectionType,
		orderInWorkoutPlan,
		name: null,
		notes: null,
		rounds,
		durationSeconds,
		intervalSeconds,
		trackTime: false,
		updatedAt: now,
	};
}

const sectionPlanAdapter = createEntityAdapter<SectionPlanInfo>({
	sortComparer: (a, b) => a.orderInWorkoutPlan - b.orderInWorkoutPlan,
});

const sectionPlanSlice = createSlice({
	name: 'entities/sectionPlan',
	initialState: sectionPlanAdapter.getInitialState(),
	reducers: {
		addedOne: sectionPlanAdapter.addOne,
		updatedOne: sectionPlanAdapter.updateOne,
		removedOne: sectionPlanAdapter.removeOne,
		removedMany: sectionPlanAdapter.removeMany,
	},
});
type SectionPlanSliceState = ReturnType<typeof sectionPlanAdapter.getInitialState>;

const sectionPlanSliceActions = { ...sectionPlanSlice.actions };
const sectionPlanSliceReducer = sectionPlanSlice.reducer;

export {
	createNewSectionPlan,
	sectionPlanSliceActions,
	sectionPlanSliceReducer,
	sectionPlanAdapter,
};
export type { SectionPlanSliceState };
